import PageTemplate, { PageData } from "../../components/feature/sections/PageTemplate";
import { SERVICES, GIVING_BACK_IMAGE } from "../../mocks/siteData";

const aboutData: PageData = {
  hero: {
    eyebrow: "Our Story",
    headline: "Built on Purpose. Designed to Empower.",
    subheadline:
      "The Lash Bar was founded by Melissa Watts in Lehi, Utah with a singular mission: to uplift women — in our community and around the world — through the transformative power of beauty.",
    primaryCTA: {
      label: "Book Your Consultation",
      href: "https://dashboard.boulevard.io/booking/businesses/1dc117c9-31f1-49a3-9094-6719fd2f14a0/widget#/cart/menu",
      external: true,
    },
    secondaryCTA: { label: "Our Services", href: "/services" },
    bgImage:
      "https://public.readdy.ai/ai/img_res/b0c3f104451918b248e256e0c5bd8355.jpg",
  },

  services: {
    eyebrow: "What We Offer",
    headline: "Services Born from Passion",
    subheadline:
      "Every service at The Lash Bar reflects Melissa's vision of a space where every woman feels valued, beautiful, and empowered — from the inside out.",
    items: SERVICES,
  },

  aiBlock: {
    eyebrow: "What We Offer",
    headline: "More Than a Beauty Studio",
    description:
      "The Lash Bar is a purpose-driven luxury beauty studio in Lehi, Utah. Founded by Melissa Watts, every service booking and boutique purchase helps fund humanitarian trips around the world. We believe beauty is a vehicle for impact — and every client who walks through our doors becomes part of something much larger.",
    whatWeDo: [
      {
        title: "Founded on Purpose",
        text: "Melissa Watts opened The Lash Bar to create a studio that transcends beauty — a place that empowers the women it serves locally while funding meaningful global humanitarian missions. Purpose is built into every decision we make, every day.",
      },
      {
        title: "Utah's Best Lash Extensions",
        text: "Voted Utah's best eyelash extensions by Utah Valley Magazine. Our VIP and Diamond certified lash artists deliver precision artistry and premium results to clients from across Utah County, Salt Lake City, and the Wasatch Front.",
      },
      {
        title: "Elite Certified Team",
        text: "Our team includes VIP and Diamond Lash Artists, expert hair stylists, and experienced tanning specialists — each passionate, fully certified, and dedicated to delivering a flawless luxury experience for every single client.",
      },
      {
        title: "Community & Global Impact",
        text: "Since opening, founder Melissa Watts has participated in over a dozen humanitarian trips worldwide — made possible entirely by the support of The Lash Bar community. Your beauty investment creates real, meaningful change.",
      },
    ],
    benefits: [
      { title: "Voted Utah's Best Lashes", text: "Recognized statewide as the premiere lash studio in Utah." },
      { title: "4.9 Stars · 901 Reviews", text: "Consistently five-star experiences, client after client." },
      { title: "Giving Back Since Day One", text: "Over a dozen global humanitarian trips funded by The Lash Bar." },
      { title: "Loyalty Rewards", text: "Every visit earns points redeemable for boutique discounts." },
    ],
  },

  process: {
    eyebrow: "Our Values",
    headline: "The Foundation of The Lash Bar",
    steps: [
      {
        number: "01",
        title: "Excellence",
        description: "We never compromise on quality. Every lash, every cut, every tan is delivered to the highest possible standard — because our clients deserve genuinely nothing less.",
        icon: "ri-medal-line",
      },
      {
        number: "02",
        title: "Empowerment",
        description: "We believe every woman deserves to feel her absolute best — confident, beautiful, and deeply valued. That belief drives every single service we deliver in this studio.",
        icon: "ri-women-line",
      },
      {
        number: "03",
        title: "Community",
        description: "We show up for clients in Lehi and Utah County — and for communities around the world through our giving-back missions and ongoing humanitarian partnerships.",
        icon: "ri-team-line",
      },
      {
        number: "04",
        title: "Gratitude",
        description: "Every client who books with us helps create something larger than beauty alone. Thank you for being part of our mission — you make every humanitarian trip possible.",
        icon: "ri-heart-3-line",
      },
    ],
  },

  whyChoose: {
    eyebrow: "Our Team",
    headline: "The People Behind the Magic",
    items: [
      {
        icon: "ri-user-star-line",
        title: "Melissa Watts — Founder",
        description: "Melissa built The Lash Bar to empower women and fund global missions. Her vision — that beauty and purpose belong together — drives every decision made in this studio.",
      },
      {
        icon: "ri-scissors-cut-line",
        title: "Shantell Douros — Manager & Hair Stylist",
        description: "A cornerstone of The Lash Bar team, Shantell manages daily operations while delivering exceptional hair styling services to clients who trust her expertise.",
      },
      {
        icon: "ri-eye-line",
        title: "VIP & Diamond Lash Artists",
        description: "Haeli Jae, Halley, Monica, Amanda, and our full lash team hold the industry's highest certification tiers — ensuring every client receives a truly elite experience.",
      },
      {
        icon: "ri-sparkling-2-line",
        title: "Full-Service Specialists",
        description: "From lash and hair artists to guest services — every team member is trained, certified, and genuinely passionate about delivering a five-star experience.",
      },
    ],
    image: GIVING_BACK_IMAGE,
  },

  serviceAreas: {
    eyebrow: "Serving the Wasatch Front",
    headline: "Serving Lehi, Utah and Surrounding Areas",
    description:
      "Clients travel from across Salt Lake City, Utah County, and beyond to experience The Lash Bar — because purpose-driven, premium beauty is always worth the journey to Lehi.",
  },

  testimonials: {
    eyebrow: "Client Love",
    headline: "Stories from Our Community",
  },

  faq: {
    eyebrow: "About The Lash Bar",
    headline: "Common Questions About Us",
    items: [
      {
        question: "Who founded The Lash Bar?",
        answer:
          "The Lash Bar was founded by Melissa Watts in Lehi, Utah with the mission to empower women and give back globally. Since opening, Melissa has participated in over a dozen humanitarian trips worldwide — funded entirely through the support of The Lash Bar community through services and boutique purchases.",
      },
      {
        question: "Where is The Lash Bar located?",
        answer:
          "The Lash Bar is at 3397 N 1200 E Suite 110, Lehi, UT 84043 — centrally positioned to serve clients from Salt Lake City, American Fork, Pleasant Grove, Orem, and across all of Utah County. We're easy to find with ample on-site parking available for every client.",
      },
      {
        question: "How does The Lash Bar give back to the community?",
        answer:
          "Every service and boutique purchase helps fund global humanitarian missions. Founder Melissa Watts has joined dozens of international outreach trips since opening. When you invest in your beauty at The Lash Bar, you directly contribute to meaningful, lasting change in communities worldwide.",
      },
      {
        question: "How long has The Lash Bar been serving Utah clients?",
        answer:
          "The Lash Bar has served the Lehi and Utah County community for several years, growing into the most recognized and celebrated lash studio in Utah. Voted Utah's Best Lashes and rated 4.9 stars from 901 Google reviews — our track record speaks for itself.",
      },
      {
        question: "Does The Lash Bar have a loyalty rewards program?",
        answer:
          "Yes — every service and boutique purchase earns loyalty points at The Lash Bar. Points accumulate over time and can be redeemed for discounts in our on-site boutique. It's our genuine way of thanking clients for being part of our beautiful community.",
      },
      {
        question: "Can I shop beauty products at The Lash Bar?",
        answer:
          "Absolutely. Our on-site boutique carries premium brands including Kérastase, RVCA, and Mangiacotti. You can also shop online through our boutique store. Every purchase earns loyalty points and helps fuel our studio's ongoing global humanitarian mission.",
      },
    ],
  },

  finalCTA: {
    eyebrow: "Join the Community",
    headline: "Be Part of Something Beautiful",
    subtext:
      "Book your appointment at The Lash Bar in Lehi, Utah and join a community that believes beauty and purpose belong together — serving clients from Salt Lake City and all of Utah County.",
  },
};

export default function AboutPage() {
  return <PageTemplate data={aboutData} />;
}
